// components/EmailDeliveryBadge.js
// ─────────────────────────────────────────────────────────────────────────────
// Reusable "did the email actually arrive?" pill.
//
// Drop it next to any "we emailed X" confirmation and hand it the
// `deliveryRefId` the backend returned for that send:
//
//   <EmailDeliveryBadge refId={result.deliveryRefId} />
//
// It polls GET /api/mail/delivery/:refId on a decaying schedule (3s, 10s,
// 30s, 75s, 150s, then stops — bounces almost always arrive inside two
// minutes) and renders one of four honest states:
//
//   SENT       "Sent — awaiting delivery confirmation"     (still polling)
//   DELIVERED  "Delivered"                                  (terminal, green)
//   FAILED     "Not delivered — <reason>"                   (terminal, red)
//   UNKNOWN    "Sent (delivery tracking not configured)"    (webhook off)
//
// It never invents certainty: UNKNOWN appears only when the server says the
// provider webhook isn't configured, so "no news" genuinely means no news
// will ever come. Renders nothing without a refId, so call sites can pass
// whatever the API returned without guarding.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { apiMailDeliveryStatus } from "../api";

const POLL_DELAYS_MS = [3000, 10000, 30000, 75000, 150000];

const TONES = {
  DELIVERED: { dot: "#2e7d32", bg: "#e8f5e9", border: "#c8e6c9", text: "#1b5e20" },
  FAILED: { dot: "#c62828", bg: "#fdecea", border: "#f5c6cb", text: "#8c1c13" },
  SENT: { dot: "#2c7be5", bg: "#eaf2ff", border: "#cfe0fb", text: "#1a4f9c" },
  UNKNOWN: { dot: "#8898aa", bg: "#f1f3f7", border: "#dde3ec", text: "#4a5568" },
};

const label = (s) => {
  if (!s) return null;
  if (s.verdict === "DELIVERED") return "Delivered";
  if (s.verdict === "FAILED") {
    if (s.status === "BOUNCED") return "Not delivered — bounced, check the address";
    if (s.status === "DROPPED") return "Not delivered — refused by the mail provider";
    return `Not delivered${s.reason ? ` — ${s.reason}` : ""}`;
  }
  if (s.verdict === "UNKNOWN") return "Sent (delivery tracking not configured)";
  return "Sent — awaiting delivery confirmation";
};

const EmailDeliveryBadge = ({ refId, style }) => {
  const [status, setStatus] = useState(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setStatus(null);
    if (!refId) return undefined;

    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await apiMailDeliveryStatus(refId);
        if (cancelledRef.current) return;
        setStatus(data);
        // Terminal or hopeless: stop polling.
        if (data.verdict === "DELIVERED" || data.verdict === "FAILED" || data.verdict === "UNKNOWN") return;
      } catch (e) {
        if (cancelledRef.current) return;
        // 404 = row not written yet (fire-and-forget log); keep trying.
      }
      if (attempt < POLL_DELAYS_MS.length) {
        timerRef.current = setTimeout(poll, POLL_DELAYS_MS[attempt]);
        attempt += 1;
      }
    };
    poll();

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refId]);

  if (!refId || !status) return null;
  const tone = TONES[status.verdict] || TONES.SENT;
  const pending = status.verdict === "SENT";

  return (
    <span
      title={status.reason || undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
        borderRadius: 20,
        padding: "3px 11px",
        fontSize: 11.5,
        fontWeight: 700,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: tone.dot,
          flexShrink: 0,
          animation: pending ? "emailDeliveryPulse 1.6s ease-in-out infinite" : "none",
        }}
      />
      {label(status)}
      {pending && (
        <style>{`@keyframes emailDeliveryPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }`}</style>
      )}
    </span>
  );
};

export default EmailDeliveryBadge;
